import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import campaignService from '../services/campaignService';
import { useInsufficientCredits } from '../contexts/InsufficientCreditsContext';

/**
 * Custom hook for table operations (delete, export)
 * Handles optimistic updates and rollbacks
 * 
 * @param {string} campaignId - Campaign ID
 * @param {string} activeVersionId - Active version ID
 * @param {Function} onDataUpdate - Callback to update data after operations
 * @param {Function} withCreditWarning - Optional credit warning wrapper function
 * @param {Function} onDataRefresh - Optional callback to refresh data after operations
 * @param {Object} listRef - Optional ref to the virtualized list for scroll control
 * @returns {Object} - Operation methods and state
 */
const useTableOperations = (campaignId, activeVersionId, onDataUpdate, withCreditWarning = null, onDataRefresh = null, listRef = null) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [deletingEntityId, setDeletingEntityId] = useState(null);
  const toast = useToast();
  const { showInsufficientCreditsModal } = useInsufficientCredits();

  /**
   * Delete a single entity
   * 
   * @param {string} entityId - Entity ID to delete
   * @param {string} entityType - Entity type ('companies' or 'contacts')
   * @param {Object} entityData - Entity data (for rollback)
   */
  const handleDelete = useCallback(async (entityId, entityType, entityData) => {
    if (deletingEntityId) return; // Prevent multiple simultaneous deletions

    if (!activeVersionId) {
      toast({
        title: 'Error',
        description: 'No version ID available for deletion. Please refresh the page.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setDeletingEntityId(entityId);
    
    try {
      // Optimistically remove the entity from UI immediately
      if (onDataUpdate) {
        onDataUpdate(entityType, (prev) => prev.filter(item => item.id !== entityId), -1);
      }

      // Use campaignService for deletion
      await campaignService.removeEntities(
        campaignId,
        [entityId],
        activeVersionId,
        entityType
      );
      
      // Success! Show confirmation
      const entityName = entityType === 'companies' ? `company "${entityData.company_name}"` :
                        `contact "${entityData.first_name} ${entityData.last_name}"`;
      toast({
        title: 'Entity Deleted',
        description: `Successfully deleted ${entityName}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Scroll to top after deletion
      if (listRef?.current) {
        setTimeout(() => {
          listRef.current?.scrollToItem?.(0, 'start');
        }, 0);
      }

    } catch (error) {
      console.error('Error deleting entity:', error);
      
      // Rollback the optimistic update on failure
      if (onDataUpdate) {
        onDataUpdate(entityType, (prev) => {
          const newData = [...prev];
          newData.unshift(entityData); // Add back to the top
          return newData;
        }, 1);
      }
      
      const entityName = entityType === 'companies' ? `company "${entityData.company_name}"` :
                        `contact "${entityData.first_name} ${entityData.last_name}"`;
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || `Failed to delete ${entityName}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingEntityId(null);
    }
  }, [campaignId, activeVersionId, deletingEntityId, onDataUpdate, toast]);

  /**
   * Delete multiple selected entities
   * 
   * @param {Set} selectedIds - Set of selected entity IDs
   * @param {string} entityType - Entity type ('companies' or 'contacts')
   */
  const handleDeleteSelected = useCallback(async (selectedIds, entityType) => {
    if (selectedIds.size === 0) return;

    if (!activeVersionId) {
      toast({
        title: 'Error',
        description: 'No version ID available for deletion. Please refresh the page.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsDeleting(true);
    const selectedArray = Array.from(selectedIds);

    try {
      // Use campaignService for deletion
      await campaignService.removeEntities(
        campaignId,
        selectedArray,
        activeVersionId,
        entityType
      );

      // Remove from UI optimistically
      if (onDataUpdate) {
        onDataUpdate(entityType, (prev) => prev.filter(item => !selectedIds.has(item.id)), -selectedIds.size);
      }

      toast({
        title: 'Success',
        description: `${selectedIds.size} ${entityType} deleted successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Scroll to top after bulk deletion
      if (listRef?.current) {
        setTimeout(() => {
          listRef.current?.scrollToItem?.(0, 'start');
        }, 0);
      }

      return true; // Signal success
    } catch (error) {
      console.error('Error deleting entities:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete entities',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [campaignId, activeVersionId, onDataUpdate, toast]);

  /**
   * Export campaign data to Excel
   */
  const handleExport = useCallback(async () => {
    if (!campaignId) {
      toast({
        title: 'Error',
        description: 'No campaign ID available to export',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!activeVersionId) {
      toast({
        title: 'Error',
        description: 'No version ID available for export. Please refresh the page.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const performExport = async () => {
      try {
        // Use campaignService for export
        const response = await campaignService.exportToXlsx(campaignId, activeVersionId);

        // Check if this is a 403 signup-required response (API interceptor resolves instead of rejects)
        // When 403 occurs, response.data will be null (from API interceptor)
        if (!response.data || response.data === null) {
          // Likely a 403 signup-required - signup modal already triggered by API interceptor
          // Don't show toast or attempt download
          return;
        }

        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Extract filename from Content-Disposition header
        let filename = `campaign_${campaignId}_v${activeVersionId}.xlsx`; // Fallback filename
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          // Try to match filename="something.xlsx" (with quotes)
          let match = contentDisposition.match(/filename="([^"]+)"/);
          if (match && match[1]) {
            filename = match[1];
          } else {
            // Try without quotes: filename=something.xlsx
            match = contentDisposition.match(/filename=([^;]+)/);
            if (match && match[1]) {
              filename = match[1].trim();
            }
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast({
          title: 'Export Complete',
          description: 'Campaign data has been downloaded as xlsx',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Dispatch custom event to notify header to refresh subscription status
        window.dispatchEvent(new CustomEvent('exportCompleted'));
        
        // Refresh table data to show updated enriched status
        // Export marks all unenriched contacts as enriched in the database
        if (onDataRefresh) {
          await onDataRefresh();
          
          // Scroll to top after data refresh
          if (listRef?.current) {
            setTimeout(() => {
              listRef.current?.scrollToItem?.(0, 'start');
            }, 0);
          }
        }
        
      } catch (error) {
        console.error('Error downloading xlsx:', error);
        
        // Handle insufficient credits error (402) - show modal instead of toast
        if (error.response?.status === 402) {
          let errorDetail = 'You do not have enough credits to export this campaign.';
          
          try {
            // If the response data is a Blob, we need to read it as text
            if (error.response.data instanceof Blob) {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorDetail = errorData.detail || errorDetail;
            } else if (error.response?.data?.detail) {
              errorDetail = error.response.data.detail;
            }
          } catch (parseError) {
            console.error('Error parsing error response:', parseError);
          }
          
          showInsufficientCreditsModal(errorDetail);
          return;
        }
        
        // Handle trial user restriction (403) - signup modal is shown by API interceptor, no toast
        if (error.response?.status === 403) {
          // Silently handle - signup modal will be shown by the API interceptor
          return;
        }
        
        // Default error toast for non-402/403 errors
        toast({
          title: 'Export Failed',
          description: error.response?.data?.detail || 'Failed to download campaign data as xlsx',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    // Show spinner for the duration of the download request
    setIsExporting(true);
    try {
      if (withCreditWarning) {
        return await withCreditWarning(
          'export',
          {
            campaign_id: campaignId,
            version_id: activeVersionId,
            qty: 1,
          },
          performExport
        );
      } else {
        return await performExport();
      }
    } finally {
      setIsExporting(false);
    }
  }, [campaignId, activeVersionId, toast, showInsufficientCreditsModal, withCreditWarning, onDataRefresh]);

  return {
    isDeleting,
    isExporting,
    deletingEntityId,
    handleDelete,
    handleDeleteSelected,
    handleExport,
  };
};

export default useTableOperations;
